import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { UsersClient, type UsersSearch } from "@/features/users/UsersClient";
import { listUsers } from "@/features/users/users.api";
import { applyParamUpdates } from "@/lib/searchParams";
import { fetchSession, sessionQueryKey } from "@/lib/auth";
import { mayAccess } from "@/lib/access";

// Behind beheer itself (handing out access is never a right).
// The session is already cached by the _shell layout guard, so this resolves
// instantly; without it, back to the dashboard. An affordance/UX gate —
// the API enforces the same right regardless.
export const Route = createFileRoute("/_shell/users")({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData({
      queryKey: sessionQueryKey,
      queryFn: fetchSession,
    });
    if (!mayAccess(session, "admin")) {
      throw redirect({ to: "/dashboard" });
    }
  },
  validateSearch: (search: Record<string, unknown>): UsersSearch => ({
    q: typeof search.q === "string" && search.q ? search.q : undefined,
    role: typeof search.role === "string" && search.role ? search.role : undefined,
    status:
      search.status === "active" || search.status === "inactive" ? search.status : undefined,
    page: typeof search.page === "string" ? Number(search.page) || undefined : undefined,
  }),
  component: UsersPage,
});

function UsersPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ["users", search],
    queryFn: () =>
      listUsers({
        q: search.q,
        role: search.role,
        isActive:
          search.status === "active" ? "true" : search.status === "inactive" ? "false" : undefined,
        page: search.page,
      }),
    placeholderData: keepPreviousData,
  });

  function updateParams(updates: Record<string, string>) {
    void navigate({
      to: "/users",
      search: (prev) => applyParamUpdates(prev as Record<string, string | undefined>, updates),
    });
  }

  return (
    <UsersClient
      result={query.data}
      isLoading={query.isLoading}
      search={search}
      updateParams={updateParams}
    />
  );
}
